import { createColumnHelper } from '@tanstack/react-table';
import { SortableHeadCell, TextCell } from 'components/tables/cells';
import {
	NumberColumnProps,
	TextColumnProps,
} from 'components/tables/columnTypes';
import ProductAccessStatusCell from 'components/tables/cells/ProductAccessStatusCell';
import { toDefaultDateFormatOrNotAvailable } from 'utils/dateUtils';
import { ProductRegistrationCell } from 'components/tables/cells/ProductCell/ProductRegistrationCell';
import { sortByDate } from 'components/tables/sorting/sortByDate';
import { ProductRegistration } from 'domain/product/interfaces';

const columnHelper = createColumnHelper<ProductRegistration>();

export const productRegistrationTableColumns = {
	productRegistration: (
		userId: string,
		overrideProps?: TextColumnProps<ProductRegistration>,
	) =>
		columnHelper.accessor((row) => row.productName, {
			id: 'name',
			enableSorting: true,
			minSize: 180,
			header: ({ header }) => (
				<SortableHeadCell
					text="Application"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<ProductRegistrationCell
					productRegistration={info.row.original}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	accessStatus: (overrideProps?: NumberColumnProps<ProductRegistration>) =>
		columnHelper.accessor((row) => row.accessStatus, {
			id: 'accessStatus',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="App access status"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<ProductAccessStatusCell
					status={info.getValue()}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	dateRegistered: (overrideProps?: TextColumnProps<ProductRegistration>) =>
		columnHelper.accessor((row) => row.registeredAt, {
			id: 'dateRegistered',
			enableSorting: true,
			sortingFn: (a, b) =>
				sortByDate(a.original.registeredAt, b.original.registeredAt),
			header: ({ header }) => (
				<SortableHeadCell
					text="Registered (UTC)"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={toDefaultDateFormatOrNotAvailable(
						info.getValue(),
						'-',
					)}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	lastLogin: (overrideProps?: TextColumnProps<ProductRegistration>) =>
		columnHelper.accessor((row) => row.lastLogin, {
			id: 'lastLogin',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Last login (UTC)"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={toDefaultDateFormatOrNotAvailable(
						info.getValue(),
						'-',
					)}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	lastActive: (overrideProps?: TextColumnProps<ProductRegistration>) =>
		columnHelper.accessor((row) => row.lastActivity, {
			id: 'lastActive',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Last active (UTC)"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={toDefaultDateFormatOrNotAvailable(
						info.getValue(),
						'-',
					)}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	handledByName: (overrideProps?: TextColumnProps<ProductRegistration>) =>
		columnHelper.accessor((row) => row.handledBy?.userName, {
			id: 'handledByName',
			enableSorting: true,
			enableGlobalFilter: false,
			minSize: 100,
			header: ({ header }) => (
				<SortableHeadCell
					text="Handled by"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue() || 'N/A'}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
};

export const sortingLastLogin = (
	a: string | undefined,
	b: string | undefined,
) => {
	if (!a) {
		return -1;
	}
	if (!b) {
		return 1;
	}

	return a > b ? 1 : -1;
};
